<template>
    <ModalContent stitch :help-content-id="`popup-customer-${current_entity_profile}`">
        <template slot="title">{{ $translate("title") }}</template>
        <template slot="content" v-if="catalog !== null">
            <template v-if="!ready">
                <Loading :absolute="false" />
            </template>
            <template v-else>
                <div class="px-4">
                    <h5 class="stitch-modal__title">
                        {{ $translate("catalogsTitle") }}
                    </h5>
                    <p
                        v-html="
                            $translate('catalogsInstructions').replace(
                                '{catalog}',
                                catalog.name
                            )
                        "
                    ></p>
                </div>
                <Table
                    :columns="columns"
                    :rows="customers"
                    :rows-count="customers_count"
                    :loading="customers_loading"
                    :filters="customers_filters"
                    :filters-conf="filtersConf"
                    :remote="true"
                    :show-select="true"
                    :item-key="'id'"
                    :item-value="'id'"
                    :value="assignedCustomers"
                    :items-per-page="customers_filters.limit"
                    @on-filters-change="onFiltersChange"
                    @on-row-click="onRowClick"
                    @on-row-selected="onRowSelected"
                    @on-row-deselected="onRowDeselected"
                    @toggle-select-all="toggleSelectAll"
                >
                    <template slot="name" slot-scope="props">
                        <CustomerName :link="false" :customer="props.row" />
                    </template>
                </Table>
            </template>
        </template>
        <template slot="bottom">
            <div class="d-flex justify-center justify-space-between align-center">
                <span class="link" @click="assignNoCustomer">{{
                    $translate("assignNoCustomer")
                }}</span>
                &nbsp;-&nbsp;
                <span class="link" @click="assignAllCustomers"
                    >{{ $translate("assignAllCustomers") }}
                </span>
            </div>
            <v-spacer />
            <v-btn class="my-2 my-sm-4 w-100 w-sm-auto" @click="hideModal">
                {{ $translate("buttons.cancel") }}
            </v-btn>
            <v-btn
                class="primary my-2 my-sm-4 w-100 w-sm-auto ml-sm-3"
                :disabled="assigning_catalog"
                @click="assignCatalogClicked"
            >
                {{ saveText }}
            </v-btn>
        </template>
    </ModalContent>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import GlobalMixins from "@/mixins/global";

export default {
    mixins: [GlobalMixins],
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapState("assigncatalog/customers", [
            "customers",
            "customers_count",
            "customers_filters",
            "customers_loading",
            "customers_loaded",
        ]),
        ...mapState("assigncatalog", [
            "customers_to_assign",
            "catalog",
            "catalog_saving",
            "catalog_saving_errors",
            "assigning_catalog",
            "ready",
        ]),
        filtersConf() {
            return [
                { preset: "search", focus: !this.isMobile },
                "billingaccount",
                "connected",
                {
                    field: "nocatalog",
                    label: this.$translate("labels.noCatalogAssigned"),
                    type: "boolean",
                },
            ];
        },

        assignedCustomers() {
            return this.customers_to_assign
                .filter((customer_id) =>
                    this.customers.find((item) => item.id == customer_id)
                )
                .map((id) => {
                    return this.customers.find((customer) => customer.id === id);
                });
        },
        columns() {
            return [
                {
                    label: this.$translate("labels.name"),
                    field: "name",
                },
                {
                    label: this.$translate("labels.code"),
                    field: "code",
                },
            ];
        },
        saveText() {
            if (this.customers_to_assign.length > 1) {
                return this.$translate("assignCustomers", {
                    count: this.customers_to_assign.length,
                });
            }
            return this.$translate("assignCatalog", {
                count: this.customers_to_assign.length,
            });
        },
    },
    methods: {
        ...mapActions("mcl", ["hideModal"]),
        ...mapMutations("assigncatalog/customers", ["updateCustomersFilters"]),
        ...mapMutations("assigncatalog", ["updateCustomersToAssign"]),
        ...mapActions("assigncatalog/customers", ["fetchCustomers"]),
        ...mapActions("assigncatalog", [
            "assignCustomers",
            "init",
            "assignAllCustomers",
            "assignNoCustomer",
        ]),
        itemSelected(item, toto) {
            if (this.customers_to_assign.indexOf(item.id) == -1) {
                this.updateCustomersToAssign(this.customers_to_assign.concat([item.id]));
            }
        },
        toggleSelectAll() {
            // If this.customers is contained on this.customers_to_assign, deselect in this.customers_to_assign items that are in this.customers
            // else, add all catalogs available on this.customers in this.customers_to_assign
            const not_in_allowed = this.customers.filter(
                (item) => !this.customers_to_assign.includes(item.id)
            );
            if (not_in_allowed.length) {
                // Select all
                this.updateCustomersToAssign(
                    this.customers_to_assign.concat(not_in_allowed.map((item) => item.id))
                );
            } else {
                // Deselect all
                this.updateCustomersToAssign(
                    this.customers_to_assign.filter(
                        (item) => !this.customers.find((catalog) => catalog.id === item)
                    )
                );
            }
        },

        onFiltersChange(filters) {
            this.updateCustomersFilters(filters);
            this.fetchCustomers();
        },
        onRowSelected(catalog) {
            if (this.customers_to_assign.indexOf(catalog.id) == -1) {
                this.updateCustomersToAssign(
                    this.customers_to_assign.concat([catalog.id])
                );
            }
        },
        onRowDeselected(catalog) {
            this.updateCustomersToAssign(
                this.customers_to_assign.filter((item) => item != catalog.id)
            );
        },
        onRowClick(catalog) {
            let customers_to_assign = this.customers_to_assign || [];
            let index = this.customers_to_assign.indexOf(catalog.id);
            if (index == -1) {
                customers_to_assign.push(catalog.id);
            } else {
                customers_to_assign.splice(index, 1);
            }
            this.updateCustomersToAssign(customers_to_assign);
        },
        async assignCatalogClicked() {
            try {
                const response = await this.assignCustomers();
                this.addAlert(this.$translate("messages.customersAssigned"));
                this.hideModal({ result: response.data });
            } catch (error) {
                this.$swal({
                    title: this.$translate("messages.error"),
                    html: this.$utils.resultToSwalContent(this, error),
                    type: "error",
                });
            }
        },
    },
    created() {
        this.init({
            catalog: this.params.catalog,
            assign_all: this.params.assign_all,
        });
    },
    i18n: {
        messages: {
            en: {
                catalogsTitle: "Sales catalogs",
                catalogsInstructions:
                    "Please choose the sales catalog(s) you want to assign to the customer.",
                default: "Default catalogs",
                title: "Assign sales catalogs",
                assignCustomers: "Assign {count} customers",
                assignCatalog: "Assign {count} customer",
                assignAllCustomers: "Assign all customers",
                assignNoCustomer: "Assign no customer",
                buttons: {
                    cancel: "Cancel",
                },
                labels: {
                    noCatalogAssigned: "No catalog assigned",
                    name: "Name",
                    code: "Code",
                },
                messages: {
                    customersAssigned: "Customers assigned",
                    error: "Error",
                },
            },
            fr: {
                catalogsTitle: "Choisissez les clients à assigner au catalogue",
                catalogsInstructions:
                    "Choisissez dans la liste ci-dessous les clients à assigner au catalogue {catalog}.",
                default: "Catalogues par défaut",
                title: "Assigner des clients au catalogue",
                assignCustomers: "Assigner {count} clients",
                assignCatalog: "Assigner {count} client",
                assignAllCustomers: "Assigner tous les clients",
                assignNoCustomer: "Assigner aucun client",
                buttons: {
                    cancel: "Annuler",
                },
                labels: {
                    noCatalogAssigned: "Aucun catalogue assigné",
                    name: "Nom",
                    code: "Code",
                },
                messages: {
                    customersAssigned: "Clients assignés",
                    error: "Erreur",
                },
            },
        },
    },
};
</script>
